/**
 * Upload queue primitives for the multi-file document upload.
 *
 * Everything in this module is a pure function over plain data so it can be
 * unit-tested without React or a DOM. The React hooks (useFileUpload,
 * useDocumentDialog) own the state and call into these helpers.
 *
 * Client-side validation (extension, size, duplicate-in-batch) is a UX layer:
 * the backend (common.security.file_validation) remains the real gate. The
 * in-batch dedupe is NOT optional, though. The backend upload path checks for
 * an existing document and inserts it in two separate steps, so two parallel
 * uploads of byte-identical files can race each other. Dropping the second
 * copy here keeps that window closed regardless of the runner concurrency.
 */

export type UploadItemStatus =
  | 'pending'
  | 'uploading'
  | 'done'
  | 'error'
  | 'duplicate'
  | 'skipped';

export interface UploadItem {
  /** Stable client-side id (crypto.randomUUID). */
  id: string;
  file: File;
  /** Document title sent with the upload; defaults to the file name without extension. */
  title: string;
  status: UploadItemStatus;
  /** 0..100, real byte progress from XHR while uploading. */
  progress: number;
  /** Localized error text for status 'error'. */
  error?: string;
  /** Server document id once the upload succeeded. */
  documentId?: string;
  /**
   * False for rows that failed client-side validation (type, size): a retry
   * can never succeed for them, so prepareRetry leaves them as errors.
   */
  retryable?: boolean;
}

/**
 * How many uploads run at the same time. Deliberately 1: the backend's
 * duplicate check (find_one) and insert (insert_one) are not atomic, and the
 * duplicate-resolution dialog is a modal per-file step. Raise only after the
 * backend maps DuplicateKeyError to 409 and the runner handles interleaved
 * duplicate prompts.
 */
export const UPLOAD_CONCURRENCY = 1;

/** Maximum document title length accepted by the dialog (matches the backend limit). */
export const MAX_TITLE_LENGTH = 200;

export interface ValidateFilesOptions {
  /** Maximum accepted size in bytes (from GET /api/documents/upload/capabilities). */
  maxBytes: number;
  /** Lower-case extensions including the leading dot, e.g. ".pdf". */
  allowed: ReadonlySet<string>;
  /** Translation function; receives the i18n key and interpolation values. */
  t: (key: string, options?: Record<string, unknown>) => string;
  /** Id factory, injectable for deterministic tests. */
  makeId?: () => string;
}

/** Identity of a File for in-batch dedupe: name, size and mtime. */
export function fileKey(file: File): string {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

/** Lower-case extension including the dot, or '' when the name has none. */
export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot).toLowerCase() : '';
}

/** File name without its extension, used as the default document title. */
export function stripExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

function defaultMakeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Turn raw files into queue items. Files already present in `existing` (same
 * name, size and mtime) are dropped silently; unsupported types and oversized
 * files are kept as 'error' rows so the user sees why they will not upload.
 */
export function validateFiles(
  files: readonly File[],
  existing: readonly UploadItem[],
  opts: ValidateFilesOptions
): UploadItem[] {
  const makeId = opts.makeId ?? defaultMakeId;
  const seen = new Set(existing.map((item) => fileKey(item.file)));
  const out: UploadItem[] = [];

  for (const file of files) {
    const key = fileKey(file);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);

    const ext = fileExtension(file.name);
    let error: string | undefined;
    if (!opts.allowed.has(ext)) {
      error = opts.t('documents.upload.errors.unsupportedType', { ext: ext || '-' });
    } else if (file.size > opts.maxBytes) {
      error = opts.t('documents.upload.errors.tooLarge', {
        size: (file.size / 1048576).toFixed(1),
        max: Math.round(opts.maxBytes / 1048576),
      });
    }

    out.push({
      id: makeId(),
      file,
      title: stripExtension(file.name),
      status: error ? 'error' : 'pending',
      progress: 0,
      error,
      retryable: error ? false : undefined,
    });
  }

  return out;
}

/**
 * Extract real files from a drop event. `dataTransfer.files` lists dropped
 * folders as pseudo-files (size 0, empty type); those are filtered out via
 * the entry API where the browser supports it.
 */
export function filesFromDrop(dataTransfer: DataTransfer): File[] {
  const items = Array.from(dataTransfer.items ?? []);
  if (items.length === 0) {
    return Array.from(dataTransfer.files ?? []);
  }

  const files: File[] = [];
  for (const item of items) {
    if (item.kind !== 'file') {
      continue;
    }
    const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
    if (entry?.isDirectory) {
      continue;
    }
    const file = item.getAsFile();
    if (file) {
      files.push(file);
    }
  }
  return files;
}

/** First item that still waits for its upload, in insertion order. */
export function nextPending(items: readonly UploadItem[]): UploadItem | undefined {
  return items.find((item) => item.status === 'pending');
}

export interface UploadSummary {
  total: number;
  pending: number;
  uploading: number;
  done: number;
  failed: number;
  skipped: number;
  duplicate: number;
}

export function summarize(items: readonly UploadItem[]): UploadSummary {
  const summary: UploadSummary = {
    total: items.length,
    pending: 0,
    uploading: 0,
    done: 0,
    failed: 0,
    skipped: 0,
    duplicate: 0,
  };
  for (const item of items) {
    switch (item.status) {
      case 'pending':
        summary.pending += 1;
        break;
      case 'uploading':
        summary.uploading += 1;
        break;
      case 'done':
        summary.done += 1;
        break;
      case 'error':
        summary.failed += 1;
        break;
      case 'skipped':
        summary.skipped += 1;
        break;
      case 'duplicate':
        summary.duplicate += 1;
        break;
      default:
        break;
    }
  }
  return summary;
}

/** True once nothing is left to upload or resolve. */
export function isSettled(items: readonly UploadItem[]): boolean {
  return items.every(
    (item) => item.status === 'done' || item.status === 'skipped' || item.status === 'error'
  );
}

/** True when every item finished successfully or was deliberately skipped. */
export function allSucceeded(items: readonly UploadItem[]): boolean {
  return (
    items.length > 0 &&
    items.every((item) => item.status === 'done' || item.status === 'skipped')
  );
}

/**
 * Prepare a retry: finished rows leave the list, failed rows go back to
 * 'pending' with their error cleared. Rows that failed client-side validation
 * (unsupported type, too large) carry `retryable: false` and stay as errors.
 */
export function prepareRetry(items: readonly UploadItem[]): UploadItem[] {
  return items
    .filter((item) => item.status !== 'done' && item.status !== 'skipped')
    .map((item) =>
      item.status === 'error' && item.retryable !== false
        ? { ...item, status: 'pending' as const, progress: 0, error: undefined }
        : item
    );
}

/** Immutable single-item update. */
export function patchItem(
  items: readonly UploadItem[],
  id: string,
  patch: Partial<UploadItem>
): UploadItem[] {
  return items.map((item) => (item.id === id ? { ...item, ...patch } : item));
}

/**
 * Map an upload failure to a localized message. The XHR helper and the
 * generated http-client both reject with `{ status, error: { detail } }`.
 */
export function mapUploadError(
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  const status = typeof error === 'object' && error !== null ? (error as { status?: number }).status : undefined;
  if (status === 413) {
    return t('documents.upload.errors.tooLargeServer');
  }
  if (status === 400 || status === 415) {
    return t('documents.upload.errors.rejected');
  }
  if (status === 504) {
    return t('documents.upload.errors.timeout');
  }
  if (status === 0) {
    return t('documents.upload.errors.network');
  }
  return t('documents.notification.saveFailed');
}

/** Names the OS drops into folders that are never documents. */
const IGNORED_ENTRY_NAMES = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);

function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  // readEntries returns results in batches (Chromium: 100 per call) and an
  // empty batch once the directory is exhausted.
  return new Promise((resolve, reject) => {
    const out: FileSystemEntry[] = [];
    const step = () => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(out);
          return;
        }
        out.push(...batch);
        step();
      }, reject);
    };
    step();
  });
}

function fileFromEntry(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

async function collectEntry(entry: FileSystemEntry, out: File[], depth: number): Promise<void> {
  if (depth > MAX_FOLDER_DEPTH || IGNORED_ENTRY_NAMES.has(entry.name) || entry.name.startsWith('.')) {
    return;
  }
  if (entry.isFile) {
    out.push(await fileFromEntry(entry as FileSystemFileEntry));
    return;
  }
  if (entry.isDirectory) {
    const children = await readAllEntries((entry as FileSystemDirectoryEntry).createReader());
    for (const child of children) {
      await collectEntry(child, out, depth + 1);
    }
  }
}

/** Guard against pathological trees; a SharePoint export rarely nests deeper. */
export const MAX_FOLDER_DEPTH = 8;

/**
 * Async variant of filesFromDrop that also walks dropped folders (entry API,
 * available in every current engine under the webkit prefix). Files inside
 * folders are queued flat; the extension filter in validateFiles decides what
 * survives. Falls back to the synchronous behaviour when the entry API is
 * missing.
 */
export async function filesFromDataTransfer(dataTransfer: DataTransfer): Promise<File[]> {
  const items = Array.from(dataTransfer.items ?? []);
  const entries = items
    .filter((item) => item.kind === 'file' && typeof item.webkitGetAsEntry === 'function')
    .map((item) => item.webkitGetAsEntry())
    .filter((entry): entry is FileSystemEntry => entry !== null);

  if (entries.length === 0 || !entries.some((entry) => entry.isDirectory)) {
    return filesFromDrop(dataTransfer);
  }

  // Entries must be resolved before the first await: the DataTransfer is
  // cleared once the drop event handler returns.
  const out: File[] = [];
  for (const entry of entries) {
    try {
      await collectEntry(entry, out, 0);
    } catch {
      // An unreadable entry (permissions, transient FS error) skips silently;
      // the rest of the drop still lands.
    }
  }
  return out;
}

/** Files from a paste event (screenshots, files copied in the OS explorer). */
export function filesFromClipboard(clipboardData: DataTransfer | null): File[] {
  if (!clipboardData) {
    return [];
  }
  const files = Array.from(clipboardData.files ?? []);
  if (files.length > 0) {
    return files;
  }
  return Array.from(clipboardData.items ?? [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);
}
